import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import EditIcon from '@mui/icons-material/Edit';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import { Button, Checkbox, FormControlLabel, IconButton } from '@mui/material';
import classNames from 'classnames';
import { DomEvent, LatLngExpression } from 'leaflet';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AccountContext } from '../context/account';
import { ProjectItemBase } from '../core/global';
import styles from '../styles/components/projects-toggle.module.scss';
import { ExportModal } from './export-modal';
import { MenuAccordion } from './menu-accordion';
import { ProjectItem, ProjectItemPurchaseRequest } from './project-item';
import { CreateProjectModal, EditProjectModal } from './project-selector';
import { StyledMarkdown } from './typeface';

const showExportButton = true;

const addOrRemovePoints = (items: ProjectItemBase[], expanded: boolean, setProjectPoints: React.Dispatch<React.SetStateAction<LatLngExpression[]>>) => {
  return
  
  // THIS below has been commented out
  // the feature was only showing data point of open project 
  // which wasn't really intuitive for users

  // const points: LatLngExpression[] = items.map( item => {
  //   const {lat, lon} = item
  //   if (lat === null || lon === null) {
  //     return null
  //   }
  //   return [lat, lon]
  // }).filter( (item) => item !== null) as LatLngExpression[]

  // setProjectPoints( (currentPoints: LatLngExpression[]) => {
  //   if (expanded) {
  //     return addPoints(currentPoints, points)
  //   }
  //   return removePoints(currentPoints, points)
  // })
}

export const ProjectsToggle = () => {
  const [currentProjectId, setCurrentProjectId] = useState<number | string | null>(null)
  const [showExportModal, setShowExportModal] = useState<boolean>(false)
  
  const {
    projects,
    refetchProjects,
    refetchUserProjects,
    userProjects,
    setProjectPoints,
    currentUserPurchaseRequests,
    setShowProjectPointsOnMap,
    showProjectPointsOnMap,
  } = React.useContext(AccountContext);

  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (ref.current) {
      DomEvent.disableClickPropagation(ref.current);
    }
    return () => {
      ref.current = null;
    }
  }, [ref]);

  const useProjects = useMemo(()=> {

    const result2: {
      [key: string]: any
    } = {
      Uncategorised: {
        name: 'Uncategorised',
        id: null,
        data: []
      },
      API: {
        name: 'Api requests',
        id: null,
        data: []
      }
    }

    userProjects.forEach( ({name, id, shared_by_uid, description}) => {
      const useName = `${name === null ? '' : name}${shared_by_uid ? ' (shared with you)' : ''}` 
      const viewOnly = shared_by_uid ? true : false
      result2[id] = {
        name: useName,
        id,
        viewOnly,
        description,
        data: []
      }
    })


    projects.forEach( item => {
      const {
        project_id,
        is_api_acquisition,
      } = item
      
      let key = `Uncategorised`
      if (project_id) {
        key = project_id.toString()
      } else if (is_api_acquisition) {
        key = 'API'
      }
      
      let useKey = key
      if (!Object.keys(result2).includes(key)) {
        useKey = 'Uncategorised'
      }
      result2[useKey] = {
        ...result2[useKey],
        data: [
          ...result2[useKey].data,
          item
        ]
      }
    })
    
    if (result2['API'] && result2['API'].data.length === 0) {
      delete result2['API']
    }

    const values = Object.values(result2).sort((a, b) => {
      if (a.name === 'Uncategorised' && b.name !== 'Uncategorised') {
        return 1
      }
      if (a.name !== 'Uncategorised' && b.name === 'Uncategorised') {
        return -1
      }
      return a.name.localeCompare(b.name)
    })

    return values
  }, [projects, userProjects])

  return (
    <>
      <div className={classNames(styles.wrapper)}>
        {useProjects.map((projectItem) => {
          const id = projectItem ? projectItem.id : null
          const useName = projectItem ? projectItem.name : ''
          const projectItems = projectItem ? projectItem.data : []
          const viewOnly = projectItem ? projectItem.viewOnly : false

          return (
            <div key={`${id}-${useName}`}>
              <MenuAccordion
                labelButtons={<>
                  {useName !== "Uncategorised" && viewOnly !== true && useName !== 'Api requests' &&
                    <IconButton
                      size="small"
                      color="success"
                      onClick={(e: React.SyntheticEvent) => {
                        e.preventDefault()
                        e.stopPropagation()
                        setCurrentProjectId(id)
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                  }
                </>}
                onChange={(expanded: boolean) => {
                  addOrRemovePoints(projectItems as ProjectItemBase[], expanded, setProjectPoints)
                }}
                label={useName}
                id={`${useName}-${id}`}
                counter={projectItems.length}
              >
                {projectItem?.description && viewOnly === true && 
                  <StyledMarkdown className={styles.description}>
                    {`${projectItem.description}  
*This folder is shared with you and is read only*`}
                  </StyledMarkdown>
                }
                {projectItems.length === 0 ?
                  <p>- empty project - </p>
                :
                  <ProjectItem viewOnly={viewOnly} key={id} data={projectItems} />
                }
              </MenuAccordion>
            </div>
          )
        })}
        {currentUserPurchaseRequests && currentUserPurchaseRequests.length > 0 &&
          <MenuAccordion
            label="Purchase requests"
            id={`purchase-requests`}
            counter={currentUserPurchaseRequests.length}
            onChange={(expanded: boolean) => {
              addOrRemovePoints(currentUserPurchaseRequests as ProjectItemBase[], expanded, setProjectPoints)
            }}
          >
            {currentUserPurchaseRequests.map( item => {
              const {id} = item
              return (
                <div key={id}>
                  <ProjectItemPurchaseRequest data={[item]} />
                </div>
              )
            })}
          </MenuAccordion>
        }
        <div ref={ref} className={styles.row}>
          <Button
            id="new-project-button"
            variant="outlined"
            size="small"
            className={styles.button}
            startIcon={<CreateNewFolderIcon sx={{color: '#ffffff'}} />}
            onClick={() => {
              setCurrentProjectId("new")
            }}
            // startIcon={<AddIcon sx={{color: '#010824'}} />}
          >
            New folder
          </Button>
          {showExportButton && <Button
            size="small"
            id="export-project-button"
            variant="contained"
            color="success"
            className={classNames(styles.button, styles.activeButton)}
            startIcon={<MergeTypeIcon />}
            onClick={() => {
              setShowExportModal(true)
            }}
          >
            Merge
          </Button>}
          <FormControlLabel
            className={styles.checkboxLabel}
            control={
              <Checkbox
                size='small'
                value="remember"
                checked={showProjectPointsOnMap}
                onChange={(e: React.SyntheticEvent) => {
                  setShowProjectPointsOnMap(i => !i)
                }}
                color="primary"
              />}
            label={
              <>Show</>
            }
          />
        </div>

        {currentProjectId === "new" && 
          <CreateProjectModal
            onSetProjectId={() => {
              setCurrentProjectId(null)
            }}
          />
        }
        {currentProjectId !== null && currentProjectId !== "new" && 
          <EditProjectModal
            projectId={currentProjectId}
            onCancel={() => {
              setCurrentProjectId(null)
              refetchProjects()
              refetchUserProjects()
            }}
          />
        }
        {showExportModal &&
          <ExportModal onClose={() => setShowExportModal(false)} />
        }
      </div>
    </>
  )
};